/**
 * Admin-feature API layer + i18n namespace registration (EPIC-006, Phase 5).
 *
 * Registers the `admin` i18n namespace (ru/ky/en — all three first-class per
 * ADR-028; RU is fallback). Pattern mirrors `features/onboarding/api.ts` and
 * `features/account/api.ts` — we call `i18n.addResourceBundle`, never touch
 * `lib/i18n.ts`.
 *
 * Types mirror the backend Pydantic schemas in
 * `apps/api/garden/features/seller_applications/{schemas,admin_router}.py`.
 */
import i18n from "@/lib/i18n";
import { apiClient } from "@/lib/api-client";

import adminEn from "@/locales/en/admin.json";
import adminKy from "@/locales/ky/admin.json";
import adminRu from "@/locales/ru/admin.json";

export const ADMIN_NS = "admin";

let adminI18nRegistered = false;

/**
 * Register the `admin` i18n namespace (ru/ky/en) on the shared i18next
 * singleton. Idempotent + init-safe. Called at the top of every admin screen
 * render so keys are present regardless of import order.
 *
 * The `adminI18nRegistered` flag is reset if i18n is not yet initialized
 * (handles test environments where `initI18n()` runs in `beforeEach` after
 * the module first loaded with i18n uninitialized).
 */
export function registerAdminI18n(): void {
  if (!i18n.isInitialized || typeof i18n.addResourceBundle !== "function") {
    // Not yet initialized — reset the flag so we try again on the next render
    adminI18nRegistered = false;
    return;
  }
  if (adminI18nRegistered) return;
  i18n.addResourceBundle("ru", ADMIN_NS, adminRu, true, false);
  i18n.addResourceBundle("ky", ADMIN_NS, adminKy, true, false);
  i18n.addResourceBundle("en", ADMIN_NS, adminEn, true, false);
  adminI18nRegistered = true;
}

// Best-effort eager registration for the production path.
registerAdminI18n();

// ─────────────────────────── Types ─────────────────────────────────────────

/** One row in the admin seller-applications list (mirrors SellerApplicationListItem). */
export interface SellerApplicationListItem {
  id: string;
  status: string;
  source: string;
  business_name: string;
  market_slug: string;
  pavilion_number: string;
  phone_e164: string;
  seller_id: string | null;
  proof_photo_s3_key: string | null;
  submitted_at: string;
}

/** Queue count buckets (mirrors QueueCounts). */
export interface QueueCounts {
  submitted: number;
  in_review: number;
  approved_today: number;
  rejected_today: number;
}

/** List response from GET /admin/seller-applications. */
export interface SellerApplicationListResponse {
  items: SellerApplicationListItem[];
  next_cursor: string | null;
  counts: QueueCounts;
}

/** Counts-only response from GET /admin/seller-applications/counts. */
export type QueueCountsResponse = QueueCounts;

/** Full detail from GET /admin/seller-applications/{id}. */
export interface SellerApplicationDetail {
  id: string;
  status: string;
  source: string;
  business_name: string;
  market_slug: string;
  pavilion_number: string;
  phone_e164: string;
  proof_photo_s3_key: string | null;
  proof_photo_url: string | null;
  proof_code: string;
  seller_id: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  raw_payload: Record<string, unknown> | null;
  created_at: string;
  updated_at: string;
}

/** Generic ack for action endpoints. */
export interface ActionAckResponse {
  ok: boolean;
  status: string;
}

/** Approve response (the backend returns only `seller_id`). */
export interface ApproveResponse {
  seller_id: string;
}

// ─────────────────────────── Query keys ────────────────────────────────────

export const adminQueryKeys = {
  applications: (filters: Record<string, unknown>) =>
    ["admin", "seller-applications", filters] as const,
  counts: () => ["admin", "seller-applications", "counts"] as const,
  detail: (id: string) => ["admin", "seller-applications", id] as const,
} as const;

// ─────────────────────────── API fetchers ──────────────────────────────────

export interface ListApplicationsParams {
  statuses?: string[];
  source?: string | null;
  cursor?: string | null;
  limit?: number;
}

export function listSellerApplications(
  params: ListApplicationsParams,
): Promise<SellerApplicationListResponse> {
  const query = new URLSearchParams();
  if (params.statuses?.length) {
    // The backend reads `Query(alias="status")`; emit the singular param name
    // (multi-append for the array — one `status=` entry per value).
    params.statuses.forEach((s) => query.append("status", s));
  }
  if (params.source) query.set("source", params.source);
  if (params.cursor) query.set("cursor", params.cursor);
  if (params.limit) query.set("limit", String(params.limit));
  const qs = query.toString();
  return apiClient.get<SellerApplicationListResponse>(
    `/admin/seller-applications${qs ? `?${qs}` : ""}`,
  );
}

export function getQueueCounts(): Promise<QueueCountsResponse> {
  return apiClient.get<QueueCountsResponse>("/admin/seller-applications/counts");
}

export function getApplicationDetail(id: string): Promise<SellerApplicationDetail> {
  return apiClient.get<SellerApplicationDetail>(`/admin/seller-applications/${id}`);
}

export function approveApplication(id: string): Promise<ApproveResponse> {
  return apiClient.post<ApproveResponse, Record<string, never>>(
    `/admin/seller-applications/${id}/approve`,
    {},
  );
}

export function rejectApplication(
  id: string,
  reason: string,
): Promise<ActionAckResponse> {
  return apiClient.post<ActionAckResponse, { reason: string }>(
    `/admin/seller-applications/${id}/reject`,
    { reason },
  );
}

export function requestReupload(
  id: string,
  reason: string,
): Promise<ActionAckResponse> {
  return apiClient.post<ActionAckResponse, { reason: string }>(
    `/admin/seller-applications/${id}/request-reupload`,
    { reason },
  );
}
